'use client'
import { useEffect, useMemo } from 'react'
import type { Reservation, SailPackage } from '@/root/shared-types'
import type { SailRefParam } from '@/root/shared-types/selectTicketPage/selectTicket'
import { useCustomRouter } from '@/root/shared-hooks'
import { isPassengerDataFormHidden } from '@/root/modules/shared/SelectTicketPage/helpers/passengerDataForm/passengerDataForm'
import { getTicketLimits } from '@/root/modules/shared/SelectTicketPage/helpers/ticketLimits/ticketLimits'
import { useTicketPrices } from '@/root/modules/shared/SelectTicketPage/hooks/useTicketPrices'
import { useTicketReservation } from '@/root/modules/shared/SelectTicketPage/hooks/useTicketReservation'

export type UseSelectTicketsParams = {
  routeBaseInfo: SailPackage
  reservation?: Reservation
  sailParams?: SailRefParam[]
}

/** Everything the ticket selection page renders: prices and limits of the chosen sails and the reservation behind the cards. */
export const useSelectTickets = ({ sailParams = [], routeBaseInfo, reservation }: UseSelectTicketsParams) => {
  const { locale } = useCustomRouter()
  const { prices, pricesBack, error, isLoading, capacityThere, capacityBack, revalidateInventory, backHref } =
    useTicketPrices(sailParams)

  const firstResidentCode = useMemo(
    () => (prices?.prices ?? []).find((price) => price.priceCategory.isResident)?.priceCategory.code,
    [prices?.prices]
  )

  const { reservationError, getErrorMessage, clearReservationError, currentRequest, ...booking } = useTicketReservation(
    { sailParams, reservation, firstResidentCode, revalidateInventory }
  )

  useEffect(() => {
    if (reservationError ?? error) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [reservationError, error])

  const limits = useMemo(
    () =>
      getTicketLimits({
        prices: prices?.prices ?? [],
        capacityThere,
        capacityBack,
        reservationItems: booking.reservation?.items ?? [],
        passengerTickets: currentRequest.passengerTickets,
      }),
    [prices?.prices, capacityThere, capacityBack, booking.reservation?.items, currentRequest.passengerTickets]
  )

  const ports = useMemo(() => {
    const [from = '', to = ''] = (routeBaseInfo?.titles?.[locale] ?? '').split(' - ').map((port) => port.trim())
    return { from, to }
  }, [routeBaseInfo?.titles, locale])

  // The banner hides once closed: closing sets '' and `'' ?? error` is falsy
  const errorMessage = (reservationError ?? error) ? (reservationError ?? getErrorMessage(error)) : undefined

  return {
    ...booking,
    ...limits,
    prices,
    pricesBack,
    isLoading,
    errorMessage,
    clearError: clearReservationError,
    ports,
    backHref,
    hidePassengerDataForm: isPassengerDataFormHidden(routeBaseInfo),
  }
}
